<template>
  <!-- Not rendered at all without a pointer. Skipping only the listeners still
       left the element in the page, which means the tile is fetched, decoded
       and rasterised behind its mask for a light that can never be switched
       on. Starts false so the prerendered HTML matches the first client
       render, then flips after mount once matchMedia can be asked. -->
  <div v-if="enabled" ref="root" class="honeycomb" aria-hidden="true"/>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

// A honeycomb field that is invisible until the pointer passes over it, lit as
// though by a torch.
//
// Rather than masking a full-viewport layer, the lit patch is a small element
// that follows the cursor. Only that patch is ever painted, and its mask never
// changes — moving it is a transform. The background is counter-shifted by the
// same amount the element moves, so the comb stays locked to the screen instead
// of sliding along with the light. A viewport-sized mask animated on every
// mousemove repaints the whole screen per frame, which is the cost this avoids.

const root = ref(null);
const enabled = ref(false);

// How far the light closes the remaining gap each frame. Lower trails further
// behind; 1 would pin it to the cursor.
const EASE = 0.11;
// Below this the gap is not worth another frame, so the loop stops rather than
// running forever chasing sub-pixel remainders.
const SETTLED = 0.4;

let raf = null;
let target = null;
let current = null;
let lit = false;

function frame() {
  raf = null;
  if (!root.value || !target) return;

  const dx = target[0] - current[0];
  const dy = target[1] - current[1];
  current[0] += dx * EASE;
  current[1] += dy * EASE;

  root.value.style.setProperty('--hx', `${current[0].toFixed(1)}px`);
  root.value.style.setProperty('--hy', `${current[1].toFixed(1)}px`);

  if (Math.abs(dx) > SETTLED || Math.abs(dy) > SETTLED) {
    raf = requestAnimationFrame(frame);
  }
}

function handleMove(event) {
  target = [event.clientX, event.clientY];

  // First sighting jumps rather than gliding in from wherever the light
  // happened to be parked. Also true of the first move after coming back to
  // the tab, since handleLeave clears the position.
  if (!current) current = [...target];

  // Kept separate from the jump above. Folding the two together meant the
  // light could only ever be switched on once: after leaving the window,
  // `current` was still set, so this branch never ran again and the field
  // stayed dark until a reload.
  if (!lit) {
    lit = true;
    root.value?.classList.add('is-lit');
  }

  if (!raf) raf = requestAnimationFrame(frame);
}

// Leaving the window puts the light out rather than stranding it at the last
// position it saw. The position is dropped as well, so returning lights up
// wherever the pointer re-enters instead of sweeping across from the old spot.
function handleLeave() {
  lit = false;
  current = null;
  root.value?.classList.remove('is-lit');
}

onMounted(async () => {
  // Pointer only, and checked on both axes: `hover: hover` rules out touch,
  // and the width query rules out the stacked mobile layout even on a device
  // that reports a pointer. Without one, nothing is rendered and no listener
  // is attached, so the whole feature costs nothing.
  const pointer = window.matchMedia('(hover: hover)').matches;
  const wide = window.matchMedia('(min-width: 769px)').matches;
  if (!pointer || !wide) return;

  enabled.value = true;
  await nextTick(); // the element has to exist before anything can position it

  window.addEventListener('mousemove', handleMove, { passive: true });
  document.documentElement.addEventListener('mouseleave', handleLeave);
});

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', handleMove);
  document.documentElement.removeEventListener('mouseleave', handleLeave);
  if (raf) cancelAnimationFrame(raf);
});
</script>

<style lang="scss" scoped>
.honeycomb {
  --hx: -9999px;
  --hy: -9999px;
  --size: 330px;

  position: fixed;
  top: 0;
  left: 0;
  width: var(--size);
  height: var(--size);
  // Half the size, so the patch is centred on the cursor rather than hanging
  // off its bottom-right corner.
  margin: calc(var(--size) / -2) 0 0 calc(var(--size) / -2);
  transform: translate3d(var(--hx), var(--hy), 0);

  // Behind the page content but above the root background.
  z-index: -1;
  pointer-events: none;

  opacity: 0;
  transition: opacity 0.5s ease;

  background-image: url('/images/honeycomb.svg');
  background-repeat: repeat;
  // Cancels the element's own movement so the comb reads as a fixed field the
  // light travels over, not a patch dragged around by the cursor.
  background-position: calc(var(--size) / 2 - var(--hx)) calc(var(--size) / 2 - var(--hy));

  // Static, so it rasterises once. Falls off well before the edge, so the patch
  // never shows a seam.
  --torch: radial-gradient(
    circle at 50% 50%,
    #000 0%,
    rgba(0, 0, 0, 0.75) 32%,
    rgba(0, 0, 0, 0.28) 58%,
    transparent 74%
  );
  -webkit-mask-image: var(--torch);
  mask-image: var(--torch);

  &.is-lit {
    opacity: 0.32;
  }
}

@media (prefers-reduced-motion: reduce) {
  .honeycomb {
    transition: none;
  }
}
</style>
